import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { CountryRate } from '@/types/pricing'
import { useCurrency } from '@/hooks/useCurrency'
import { useTranslation } from 'react-i18next'
import { Flag } from '@/components/Flag'

interface AnimatedCountryRateProps {
  countries: CountryRate[]
  interval?: number
}

export function AnimatedCountryRate({ countries, interval = 4000 }: AnimatedCountryRateProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const { formatCurrency } = useCurrency()
  const { t } = useTranslation('marketing')

  useEffect(() => {
    if (countries.length === 0) return

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % countries.length)
    }, interval)

    return () => clearInterval(timer)
  }, [countries.length, interval])

  if (countries.length === 0) {
    return <span className="font-bold text-primary">...</span>
  }

  const currentCountry = countries[currentIndex]
  const lowestRate = Math.min(currentCountry.landlineRate, currentCountry.mobileRate)

  const polygons = [
    'polygon(2% 8%, 98% 3%, 99% 92%, 1% 95%)',
    'polygon(1% 5%, 97% 6%, 98% 94%, 2% 93%)',
    'polygon(3% 6%, 99% 4%, 97% 93%, 2% 96%)'
  ];
  const skews = [
    { x: -2, y: -1 },
    { x: 1.5, y: -0.5 },
    { x: -1, y: 1 }
  ];
  const variation = currentIndex % 3;
  const polygon = polygons[variation];
  const skew = skews[variation];

  return (
    <span className="inline-block align-baseline font-bold text-foreground">
      <AnimatePresence mode="wait">
        <motion.span
          key={currentIndex}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className="relative inline-flex items-center gap-2 whitespace-nowrap px-1.5 py-0.5"
        >
          <span className="absolute inset-0 bg-primary/20"
                style={{
                    clipPath: polygon,
                    transform: `skewX(${skew.x}deg) skewY(${skew.y}deg) scaleY(1.1)`
                }} />
          <Flag countryCode={currentCountry.countryCode} size="md" />
          <span className="relative">{t('pricing.rateWithUnit', { rate: formatCurrency(lowestRate) })}</span>
        </motion.span>
      </AnimatePresence>
    </span>
  )
}
